import * as vp from 'vopay';
import * as Yup from 'yup';
import { FunctionComponent, useEffect, useState } from 'react';
import { Formik } from 'formik';

import type { ChangeRoleHandler } from '../types';
import useBuildUserRoleChangeRows from './buildChangeUserRoleRows.helper';
import { useTranslation } from 'react-i18next';
import { usePartnerData } from '@/providers/PartnerData.provider';

const { Box, Button, Table, Select } = vp;

type ChangeUserRoleProps = {
  handlers: ChangeRoleHandler;
  defaultValues: vp.UserChangeRoleRequest;
  reviewing: boolean;
};

type FC = FunctionComponent<ChangeUserRoleProps>;

const ChangeUserRole: FC = (props: ChangeUserRoleProps) => {
  const { handlers, defaultValues, reviewing } = props;
  const changeRoleRow = useBuildUserRoleChangeRows();
  const [buttonIsDisabled, setButtonIsDisabled] = useState<boolean>(true);
  const { t } = useTranslation('translations', { keyPrefix: 'Users.ChangeUserRole' });
  const { t: tGlobal } = useTranslation('translations', { keyPrefix: 'Global' });

  const {
    response: { isRoleViewOnly },
  } = usePartnerData();

  return (
    <Formik
      initialValues={defaultValues}
      onSubmit={reviewing ? handlers.onChangeUserRole : handlers.onReview}
      validationSchema={createdValidationSchema()}
      validateOnChange
      validateOnBlur
    >
      {({ values, touched, errors, handleChange, handleBlur, handleSubmit }) => {
        useEffect(() => {
          const hasErrors = Object.entries(errors).length > 0;
          const filteredArr = Object.values(values).filter((value) => typeof value !== 'boolean');
          const allEmptyFields = Object.values(filteredArr).every((val) => !val);

          setButtonIsDisabled(allEmptyFields || hasErrors);
        }, [errors]);

        const changeRoleTable: vp.TableProps = {
          name: 'change-role-fields',
          variant: 'pair',
          columns: {
            Field: { header: tGlobal('Tables.labels') },
            Input: { header: tGlobal('Tables.fields') },
          },
          rows: changeRoleRow.map(({ label, id, getSelectOptions, placeholder }) => ({
            Field: label,
            Input: (
              <Select
                variant="standard"
                options={getSelectOptions()}
                key={id}
                name={id}
                value={String(values[id])}
                placeholder={placeholder}
                noOrdering
                onBlur={handleBlur}
                onChange={handleChange}
                error={errors[id] && touched[id] ? errors[id] : null}
                disabled={reviewing}
              />
            ),
          })),
        };
        return (
          <form onSubmit={handleSubmit}>
            <Table {...changeRoleTable} />
            <Box mt="md" jc="fe">
              <Button
                id="change-role-submit"
                type="submit"
                label={reviewing ? t('Review.submitButton') : t('submitButton')}
                isDisabled={buttonIsDisabled || isRoleViewOnly}
              />
            </Box>
          </form>
        );
      }}
    </Formik>
  );
  function createdValidationSchema() {
    return Yup.object().shape({
      Role: Yup.string().label(t('roleLabel')).required(),
    });
  }
};

export default ChangeUserRole;
